import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiRequest } from '../../services/apiClient';
import Button from '../ui/Button';
import { StatusPill } from '../ui/Status';
import DataTable from '../ui/DataTable';
import EmptyState from '../ui/EmptyState';
import { useConfirm } from '../ui/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';
import DirectorPanelState from './DirectorPanelState';
import DirectorModal from './DirectorModal';
import {
  DIRECTOR_DATA_UPDATED_EVENT,
  notifyDirectorAdminToast,
  notifyDirectorDataUpdated
} from '../../utils/directorEvents';
import { buildPersonWorkspacePath } from '../../config/workforceRouting';
import { useMultiFetch } from '../../hooks/useMultiFetch';
import { groupRowsByLocation, hasMultipleLocationGroups } from '../../utils/locationGrouping';
import { formatRoleLabel } from '../../config/appRoutes';

// Workforce People Model Phase 3.1 — UI labels only.
// `value` stays at the underlying manager_role_key enum
// ('ops_manager' / 'airport_manager' / 'director'); the label
// "Team Member" used to display next to ops_manager misled operators
// into picking it for hourly employees (who don't belong in the
// leadership roster). The label now matches the schema value so the
// leadership-vs-workforce boundary is unambiguous.
const MANAGER_ROLE_OPTIONS = [
  { value: 'ops_manager', label: formatRoleLabel('ops_manager') },
  { value: 'airport_manager', label: formatRoleLabel('airport_manager') },
  { value: 'director', label: formatRoleLabel('director') }
];

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  mobilePhone: '',
  locationId: '',
  managerRoleKey: 'ops_manager',
  roleTitle: '',
  department: '',
  hireDate: '',
  activeFrom: new Date().toISOString().slice(0, 10)
};
function normalizePhoneInput(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (raw.startsWith('+') && digits.length >= 10) {
    return `+${digits}`;
  }
  return raw;
}

function formatPhoneDisplay(value) {
  const normalized = normalizePhoneInput(value);
  const digits = normalized.replace(/\D/g, '');
  const localDigits = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (localDigits.length === 10) {
    return `(${localDigits.slice(0, 3)}) ${localDigits.slice(3, 6)}-${localDigits.slice(6)}`;
  }
  return normalized || 'Not set';
}

function validatePhoneValue(value) {
  const raw = String(value || '').trim();
  if (!raw) {
    return '';
  }

  const normalized = normalizePhoneInput(raw);
  const digits = normalized.replace(/\D/g, '');
  if (digits.length === 10 || (digits.length === 11 && digits.startsWith('1'))) {
    return '';
  }

  return 'Enter a valid mobile number.';
}

function formatAccessProvisioningDetail(accessProvisioning) {
  if (!accessProvisioning?.user) {
    return '';
  }

  if (accessProvisioning.mode === 'created') {
    return `Access created for ${accessProvisioning.user.email}. Temporary password shown below.`;
  }

  if (accessProvisioning.mode === 'linked_existing') {
    return `Existing access for ${accessProvisioning.user.email} was linked to this manager.`;
  }

  if (accessProvisioning.mode === 'updated_existing') {
    return `Existing access for ${accessProvisioning.user.email} was updated to match the manager role and email.`;
  }

  return `Existing access for ${accessProvisioning.user.email} is already linked.`;
}

function roleLabel(roleKey) {
  return MANAGER_ROLE_OPTIONS.find(option => option.value === roleKey)?.label || formatRoleLabel(roleKey);
}

const ALL_DIRECTOR_LOCATIONS = 'ALL';

function isGlobalDirectorManager(manager) {
  return String(manager?.managerRoleKey || '').toLowerCase() === 'director';
}

function DirectorManagersPanel({ selectedLocationCode = ALL_DIRECTOR_LOCATIONS, locationLabel = 'Company Rollup', onNavigate = null }) {
  const navigate = useNavigate();
  const { user: authUser } = useAuth() || {};
  const canViewEmployeeProfile = Array.isArray(authUser?.permissions)
    && (authUser.permissions.includes('employees.read') || authUser.permissions.includes('manage_users'));
  const confirm = useConfirm();
  const [form, setForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [locations, setLocations] = useState([]);
  const [managers, setManagers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [isProvisioningAccess, setIsProvisioningAccess] = useState(false);
  const [editingManagerId, setEditingManagerId] = useState(null);
  const [banner, setBanner] = useState('');
  const [error, setError] = useState('');
  const [provisionedAccessItems, setProvisionedAccessItems] = useState([]);
  // Workforce People Model Phase 3.3a — observability only.
  // The toggle controls whether the new Classification column is
  // rendered in the Leadership Coverage table. Default OFF so the
  // production roster view is unchanged unless an operator opts in.
  const [showWorkforceClassification, setShowWorkforceClassification] = useState(false);

  const scopedLocations = useMemo(
    () => (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
      ? locations.filter(location => location.isActive && location.code === selectedLocationCode)
      : locations.filter(location => location.isActive)),
    [locations, selectedLocationCode]
  );

  const visibleManagers = useMemo(
    () => (selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
      ? managers.filter(manager => manager.locationCode === selectedLocationCode || isGlobalDirectorManager(manager))
      : managers),
    [managers, selectedLocationCode]
  );

  const visibleActiveManagers = useMemo(
    () => visibleManagers.filter(manager => manager.status === 'active'),
    [visibleManagers]
  );
  const managerLocationGroups = useMemo(
    () => groupRowsByLocation(visibleManagers),
    [visibleManagers]
  );

  // Workforce People Model Phase 3.3a — diagnostic counts over the
  // currently scoped manager rows. Computed off `visibleManagers`
  // so the location filter still applies, but no row is hidden or
  // filtered. Hourly / salary / null are mutually exclusive buckets
  // and the four numbers always sum to `visibleManagers.length`.
  const classificationSummary = useMemo(() => {
    let hourly = 0;
    let salary = 0;
    let none = 0;
    for (const manager of visibleManagers) {
      const classification = manager?.linkedEmploymentClassification ?? null;
      if (classification === 'hourly') hourly += 1;
      else if (classification === 'salary') salary += 1;
      else none += 1;
    }
    return {
      leadershipRows: visibleManagers.length,
      hourlyLinked: hourly,
      salaryLinked: salary,
      noProfileLinked: none
    };
  }, [visibleManagers]);
  const showManagerLocationHeaders = hasMultipleLocationGroups(managerLocationGroups);

  const { data: fetchedRoster, isLoading, error: rosterError, refetch } = useMultiFetch({
    locations: '/api/locations',
    managers: '/api/managers',
  });

  useEffect(() => {
    if (fetchedRoster.locations) setLocations(fetchedRoster.locations);
    if (fetchedRoster.managers) setManagers(fetchedRoster.managers);
    if (rosterError) {
      setError(rosterError);
    } else if (fetchedRoster.locations) {
      setError('');
    }
  }, [fetchedRoster, rosterError]);

  useEffect(() => {
    if (!fetchedRoster.locations) return;
    const locationRows = fetchedRoster.locations;
    const defaultLocation = selectedLocationCode !== ALL_DIRECTOR_LOCATIONS
      ? locationRows.find(location => location.isActive && location.code === selectedLocationCode)
      : locationRows.find(location => location.isActive);
    if (defaultLocation?.id) {
      setForm(current => (
        current.locationId
          ? current
          : { ...current, locationId: String(defaultLocation.id) }
      ));
    }
  }, [fetchedRoster, selectedLocationCode]);

  useEffect(() => {
    const handler = () => refetch();
    window.addEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
    return () => window.removeEventListener(DIRECTOR_DATA_UPDATED_EVENT, handler);
  }, [refetch]);

  useEffect(() => {
    if (selectedLocationCode === ALL_DIRECTOR_LOCATIONS) {
      return;
    }

    const scopedLocation = locations.find(location => location.code === selectedLocationCode);
    if (!scopedLocation) {
      return;
    }

    setForm(current => (
      current.locationId === String(scopedLocation.id)
        ? current
        : { ...current, locationId: String(scopedLocation.id) }
    ));
  }, [locations, selectedLocationCode]);

  const handleFormChange = (field, value) => {
    setForm(current => ({ ...current, [field]: value }));
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(current => ({ ...current, [field]: value }));
  };

  const closeEditModal = () => {
    setEditingManagerId(null);
    setEditForm(EMPTY_FORM);
    setError('');
  };

  const handleCreateManager = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setError('');
    setBanner('');
    setProvisionedAccessItems([]);

    const phoneError = validatePhoneValue(form.mobilePhone);
    if (phoneError) {
      setError(phoneError);
      setIsSubmitting(false);
      return;
    }

    try {
      const created = await apiRequest('/api/managers', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          locationId: Number(form.locationId)
        })
      });

      setManagers(current => [...current, created].sort((a, b) => `${a.locationCode}${a.lastName}${a.firstName}`.localeCompare(`${b.locationCode}${b.lastName}${b.firstName}`)));
      if (created.accessProvisioning?.user) {
        setProvisionedAccessItems([{
          rowNumber: 'Manual',
          managerLabel: `${created.firstName} ${created.lastName}`,
          email: created.accessProvisioning.user.email,
          role: roleLabel(created.accessProvisioning.user.role),
          provisioningMode: created.accessProvisioning.mode,
          temporaryPassword: created.accessProvisioning.temporaryPassword || ''
        }]);
      }

      setBanner(
        `Manager ${created.firstName} ${created.lastName} added. Employee ID: ${created.employeeId}. `
        + `Manager Key (roster identifier used for CSV import matching): ${created.managerKey}. `
        + `${formatAccessProvisioningDetail(created.accessProvisioning)}`.trim()
      );
      notifyDirectorAdminToast(`Manager ${created.firstName} ${created.lastName} added. Employee ID ${created.employeeId}.`);
      notifyDirectorDataUpdated(`Director dashboard refreshed for manager changes.`);
      setForm(current => ({
        ...EMPTY_FORM,
        locationId: current.locationId || form.locationId,
        activeFrom: EMPTY_FORM.activeFrom
      }));
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to create manager.', 'error');
      setError(requestError.message || 'Unable to create manager.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenEditModal = (manager) => {
    setEditingManagerId(manager.id);
    setEditForm({
      firstName: manager.firstName || '',
      lastName: manager.lastName || '',
      email: manager.email || '',
      mobilePhone: manager.mobilePhone || '',
      locationId: String(manager.locationId || ''),
      managerRoleKey: manager.managerRoleKey || 'ops_manager',
      roleTitle: manager.roleTitle || '',
      department: manager.department || '',
      hireDate: manager.hireDate || '',
      activeFrom: manager.activeFrom || EMPTY_FORM.activeFrom
    });
    setBanner('');
    setError('');
  };

  const handleSaveManagerEdit = async (event) => {
    event.preventDefault();
    if (!editingManagerId) {
      return;
    }

    const phoneError = validatePhoneValue(editForm.mobilePhone);
    if (phoneError) {
      setError(phoneError);
      return;
    }

    setIsEditSubmitting(true);
    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/managers/${editingManagerId}`, {
        method: 'PATCH',
        body: JSON.stringify({
          firstName: editForm.firstName,
          lastName: editForm.lastName,
          email: editForm.email,
          mobilePhone: editForm.mobilePhone,
          locationId: Number(editForm.locationId),
          managerRoleKey: editForm.managerRoleKey,
          roleTitle: editForm.roleTitle,
          department: editForm.department,
          hireDate: editForm.hireDate || null
        })
      });

      setManagers(current => current.map(item => (item.id === updated.id ? updated : item)));
      setBanner(`Updated ${updated.firstName} ${updated.lastName}.`);
      notifyDirectorAdminToast(`Updated ${updated.firstName} ${updated.lastName}.`);
      notifyDirectorDataUpdated('Director dashboard refreshed for manager updates.');
      closeEditModal();
    } catch (requestError) {
      setError(requestError.message || 'Unable to update manager.');
      notifyDirectorAdminToast(requestError.message || 'Unable to update manager.', 'error');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleRegenerateManagerKey = async () => {
    if (!editingManagerId) {
      return;
    }

    const confirmed = await confirm(
      'Regenerate this Manager Key? The previous key will stop working immediately. '
      + 'Anything that references the old key (e.g. CSV roster re-imports keyed off it) will need to use the new one.',
      { title: 'Regenerate Manager Key', confirmLabel: 'Regenerate', tone: 'danger' }
    );

    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');

    try {
      const updated = await apiRequest(`/api/managers/${editingManagerId}/regenerate-key`, { method: 'PATCH' });
      setManagers(current => current.map(item => (item.id === updated.id ? updated : item)));
      setBanner(`New Manager Key for ${updated.firstName} ${updated.lastName}: ${updated.managerKey}`);
      notifyDirectorAdminToast(`Manager Key regenerated for ${updated.firstName} ${updated.lastName}.`);
    } catch (requestError) {
      setError(requestError.message || 'Unable to regenerate Manager Key.');
      notifyDirectorAdminToast(requestError.message || 'Unable to regenerate Manager Key.', 'error');
    }
  };

  const handleProvisionVisibleAccess = async () => {
    if (!visibleManagers.length) {
      return;
    }

    const confirmed = await confirm(
      `Provision user access for the ${visibleManagers.length} manager${visibleManagers.length === 1 ? '' : 's'} currently in scope? Existing linked users will be left alone unless their email or role needs syncing.`,
      { title: 'Provision manager access', confirmLabel: 'Provision' }
    );

    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');
    setProvisionedAccessItems([]);
    setIsProvisioningAccess(true);

    try {
      const response = await apiRequest('/api/managers/provision-access', {
        method: 'POST',
        body: JSON.stringify({
          managerIds: visibleManagers.map(manager => manager.id)
        })
      });

      const provisionedRows = (response.results || []).map(item => ({
        rowNumber: 'Backfill',
        managerLabel: item.managerLabel,
        email: item.email,
        role: roleLabel(item.role),
        provisioningMode: item.provisioningMode,
        temporaryPassword: item.temporaryPassword || ''
      }));

      setProvisionedAccessItems(provisionedRows);
      setBanner(
        provisionedRows.length
          ? `Access provisioning complete. ${provisionedRows.length} manager account${provisionedRows.length === 1 ? '' : 's'} were created, linked, or updated for ${locationLabel}.`
          : `No access changes were needed for ${locationLabel}.`
      );
      notifyDirectorAdminToast(
        provisionedRows.length
          ? `Access provisioning complete for ${provisionedRows.length} manager account${provisionedRows.length === 1 ? '' : 's'}.`
          : 'No access changes were needed for the current manager scope.'
      );
      notifyDirectorDataUpdated('Director dashboard refreshed for manager access provisioning.');
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to provision manager access.', 'error');
      setError(requestError.message || 'Unable to provision manager access.');
    } finally {
      setIsProvisioningAccess(false);
    }
  };

  const handleToggleManager = async (manager) => {
    const confirmed = await confirm(
      manager.status === 'active'
        ? `Deactivate ${manager.firstName} ${manager.lastName}? Historical records stay intact and future active use will stop until reactivated.`
        : `Reactivate ${manager.firstName} ${manager.lastName}? They will return to active directories and future operational use.`,
      manager.status === 'active'
        ? { title: 'Deactivate manager', confirmLabel: 'Deactivate', tone: 'danger' }
        : { title: 'Reactivate manager', confirmLabel: 'Reactivate' }
    );

    if (!confirmed) {
      return;
    }

    setError('');
    setBanner('');

    try {
      const path = manager.status === 'active'
        ? `/api/managers/${manager.id}/inactive`
        : `/api/managers/${manager.id}/active`;
      const payload = manager.status === 'active'
        ? { inactiveFrom: new Date().toISOString().slice(0, 10) }
        : { activeFrom: new Date().toISOString().slice(0, 10) };

      const updated = await apiRequest(path, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });

      setManagers(current => (
        manager.status === 'active'
          ? current.filter(item => item.id !== updated.id)
          : current.map(item => (item.id === updated.id ? updated : item))
      ));
      setBanner(
        manager.status === 'active'
          ? `Manager ${manager.firstName} ${manager.lastName} removed from active directories. Historical records remain intact.`
          : `Manager ${manager.firstName} ${manager.lastName} reactivated.`
      );
      notifyDirectorDataUpdated('Director dashboard refreshed for manager changes.');
      notifyDirectorAdminToast(
        manager.status === 'active'
          ? `Manager ${manager.firstName} ${manager.lastName} deactivated.`
          : `Manager ${manager.firstName} ${manager.lastName} reactivated.`,
        'success',
        {
          label: 'Undo',
          onClick: async () => {
            const revertPath = updated.status === 'active'
              ? `/api/managers/${updated.id}/inactive`
              : `/api/managers/${updated.id}/active`;
            const revertPayload = updated.status === 'active'
              ? { inactiveFrom: new Date().toISOString().slice(0, 10) }
              : { activeFrom: new Date().toISOString().slice(0, 10) };
            const reverted = await apiRequest(revertPath, {
              method: 'PATCH',
              body: JSON.stringify(revertPayload)
            });

            setManagers(current => (
              reverted.status === 'active'
                ? [...current.filter(item => item.id !== reverted.id), reverted].sort((a, b) => `${a.locationCode}${a.lastName}${a.firstName}`.localeCompare(`${b.locationCode}${b.lastName}${b.firstName}`))
                : current.filter(item => item.id !== reverted.id)
            ));
            setBanner(
              reverted.status === 'active'
                ? `Manager ${reverted.firstName} ${reverted.lastName} reactivated.`
                : `Manager ${reverted.firstName} ${reverted.lastName} removed from active directories. Historical records remain intact.`
            );
            notifyDirectorDataUpdated('Director dashboard refreshed for manager changes.');
            notifyDirectorAdminToast(
              reverted.status === 'active'
                ? `Manager ${reverted.firstName} ${reverted.lastName} reactivated.`
                : `Manager ${reverted.firstName} ${reverted.lastName} deactivated.`
            );
          }
        }
      );
    } catch (requestError) {
      notifyDirectorAdminToast(requestError.message || 'Unable to update manager status.', 'error');
      setError(requestError.message || 'Unable to update manager status.');
    }
  };

  return (
    <div className="director-admin-stack">
      <DirectorPanelState kind="loading" message={isLoading ? 'Loading leadership directory...' : ''} />
      <DirectorPanelState kind="error" message={error} />
      <DirectorPanelState kind="success" message={!error ? banner : ''} />

      <div className="director-split-grid">
        <section className="director-card">
          <div className="role-section-header">
            <h2>Leadership Directory</h2>
            <span>{visibleActiveManagers.length} active</span>
          </div>
          <p className="role-section-copy">
            Manage staffing, coverage, hierarchy, and operational assignments for {locationLabel}. Employee profiles are the person hub; this roster is the operational overlay.
          </p>

          <form className="director-form-grid" onSubmit={handleCreateManager}>
            <div className="director-note-card director-form-span">
              <strong>Employee ID &amp; Manager Key</strong>
              <div className="director-muted">
                Generated automatically once this manager is created — no need to enter them here.
              </div>
            </div>
            <label>
              First Name
              <input value={form.firstName} onChange={(event) => handleFormChange('firstName', event.target.value)} required />
            </label>
            <label>
              Last Name
              <input value={form.lastName} onChange={(event) => handleFormChange('lastName', event.target.value)} required />
            </label>
            <label>
              Email
              <input type="email" value={form.email} onChange={(event) => handleFormChange('email', event.target.value)} />
            </label>
            <label>
              Mobile Phone
              <input value={form.mobilePhone} onChange={(event) => handleFormChange('mobilePhone', event.target.value)} placeholder="+1 555 555 5555" />
            </label>
            <label>
              Location
              <select value={form.locationId} onChange={(event) => handleFormChange('locationId', event.target.value)} required>
                <option value="">Select location</option>
                {scopedLocations.map(location => (
                  <option key={location.id} value={location.id}>{location.code} - {location.name}</option>
                ))}
              </select>
            </label>
            <label>
              Leadership Role
              <select value={form.managerRoleKey} onChange={(event) => handleFormChange('managerRoleKey', event.target.value)}>
                {MANAGER_ROLE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <small className="director-managers-leadership-role-helper">
                Leadership roles own location KPIs, goals, and scorecards.
                For hourly and non-leadership employees, use Workforce.
              </small>
            </label>
            <label>
              Role Title
              <input value={form.roleTitle} onChange={(event) => handleFormChange('roleTitle', event.target.value)} required />
            </label>
            <label>
              Department
              <input value={form.department} onChange={(event) => handleFormChange('department', event.target.value)} />
            </label>
            <label>
              Hire Date
              <input type="date" value={form.hireDate} onChange={(event) => handleFormChange('hireDate', event.target.value)} />
            </label>
            <label>
              Active From
              <input type="date" value={form.activeFrom} onChange={(event) => handleFormChange('activeFrom', event.target.value)} required />
            </label>
            <div className="director-form-actions">
              <Button type="submit" loading={isSubmitting} loadingLabel="Saving…">
                Add Manager
              </Button>
            </div>
          </form>
        </section>

        <section className="director-card director-note-card">
          <div className="role-section-header">
            <h2>Roster Tools</h2>
            <span>Manual + Access</span>
          </div>
          <p className="role-section-copy">
            Use access provisioning for managers currently in scope. CSV import/export lives in Data Management &amp; Audit.
          </p>
          <div className="director-form-actions">
            <Button
              size="sm"
              variant="secondary"
              onClick={handleProvisionVisibleAccess}
              loading={isProvisioningAccess}
              loadingLabel="Provisioning Access..."
              disabled={visibleManagers.length === 0}
            >
              {`Provision Access for ${locationLabel}`}
            </Button>
          </div>
          <ul className="director-bullet-list">
            <li>Managers are deactivated, not deleted.</li>
            <li>History stays attached to inactive managers.</li>
            <li>Future allocations only use active managers.</li>
            <li>Email-based access can be linked automatically.</li>
            <li>Use Data Management &amp; Audit for manager roster CSV upload/export.</li>
          </ul>
          {provisionedAccessItems.length ? (
            <div className="director-history-list">
              <div className="director-history-item">
                <div className="director-history-row">
                  <strong>New Access Credentials</strong>
                  <span>{provisionedAccessItems.filter(item => item.temporaryPassword).length} temporary password{provisionedAccessItems.filter(item => item.temporaryPassword).length === 1 ? '' : 's'}</span>
                </div>
                <div className="director-muted">Temporary passwords are shown once.</div>
              </div>
              {provisionedAccessItems.map(item => (
                <div className="director-history-item" key={`${item.rowNumber}-${item.email}`}>
                  <div className="director-history-row">
                    <strong>{item.managerLabel}</strong>
                    <span>{item.role}</span>
                  </div>
                  <div className="director-muted">{item.email}</div>
                  <div className="director-muted">
                    {item.provisioningMode === 'created'
                      ? `Temporary password: ${item.temporaryPassword}`
                      : item.provisioningMode === 'linked_existing'
                        ? 'Existing access was linked automatically.'
                        : item.provisioningMode === 'updated_existing'
                          ? 'Existing access was updated to match the latest manager email and role.'
                          : 'Access was already linked.'}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </section>
      </div>

      {/*
        Workforce People Model Phase 3.3a — Leadership / Workforce
        overlap diagnostic. Operators can see at a glance how many
        rows in the Leadership Directory are linked to an hourly
        employee profile (likely workforce-compatibility stubs
        rather than real leaders). Phase 3.3a does NOT filter
        anything; this card is observability only.
      */}
      <section
        className="director-card director-managers-workforce-overlap"
        data-testid="leadership-workforce-overlap-card"
      >
        <div className="role-section-header">
          <h2>Leadership / Workforce Overlap</h2>
          <div className="role-section-actions">
            <label
              className="director-managers-classification-toggle"
              data-testid="toggle-show-workforce-classification-label"
            >
              <input
                type="checkbox"
                data-testid="toggle-show-workforce-classification"
                checked={showWorkforceClassification}
                onChange={(event) => setShowWorkforceClassification(event.target.checked)}
              />
              Show Workforce Classification
            </label>
          </div>
        </div>
        <p className="role-section-copy">
          Diagnostic only — every roster row continues to count toward
          allocations, coverage, and goals. Use this card to validate
          which leadership rows are actually linked to an hourly
          workforce profile before a future scoping pass.
        </p>
        <dl className="director-managers-overlap-grid">
          <div data-testid="overlap-stat-leadership-rows">
            <dt>Leadership Rows</dt>
            <dd>{classificationSummary.leadershipRows}</dd>
          </div>
          <div data-testid="overlap-stat-hourly-linked">
            <dt>Linked Hourly Profiles</dt>
            <dd>{classificationSummary.hourlyLinked}</dd>
          </div>
          <div data-testid="overlap-stat-salary-linked">
            <dt>Linked Salary Profiles</dt>
            <dd>{classificationSummary.salaryLinked}</dd>
          </div>
          <div data-testid="overlap-stat-no-profile">
            <dt>No Profile Linked</dt>
            <dd>{classificationSummary.noProfileLinked}</dd>
          </div>
        </dl>
      </section>

      <section className="director-card">
        <div className="role-section-header">
          <h2>Leadership Coverage</h2>
          <div className="role-section-actions">
            <span data-testid="leadership-coverage-active-count">{visibleActiveManagers.length} active</span>
          </div>
        </div>
        <DataTable
          tableClassName="director-managers-table"
          emptyMessage="No managers are visible for the current location scope."
          emptyState={(
            <EmptyState
              icon="R"
              headline="No roster yet"
              body="Add the first location leader or import a roster to start assigning goals and actuals."
              primaryCtaLabel="Add Manager"
              onPrimaryCta={() => {
                const formCard = document.querySelector('.director-form-grid');
                if (formCard && typeof formCard.scrollIntoView === 'function') {
                  formCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              compact
            />
          )}
          isEmpty={!visibleManagers.length}
          columns={[
            { label: 'Name' },
            { label: 'Role', className: 'director-managers-table-col-role' },
            { label: 'Title' },
            { label: 'Mobile', className: 'director-managers-table-col-phone' },
            { label: 'Location', className: 'director-managers-table-col-location' },
            { label: 'Hire Date', className: 'director-managers-table-col-date' },
            { label: 'Active', className: 'director-managers-table-col-status' },
            { label: 'Active Window', className: 'director-managers-table-col-window' },
            // Workforce People Model Phase 3.3a — optional column
            // gated by the Show Workforce Classification toggle.
            ...(showWorkforceClassification
              ? [{ label: 'Classification', className: 'director-managers-table-col-classification' }]
              : []),
            { label: 'Actions', className: 'director-managers-table-col-actions' }
          ]}
        >
          {managerLocationGroups.map(group => (
            <React.Fragment key={group.key}>
              {showManagerLocationHeaders ? (
                <tr className="director-location-group-row">
                  <td colSpan={showWorkforceClassification ? 10 : 9}>
                    <span>{group.label}</span>
                    <strong>{group.rows.length}</strong>
                  </td>
                </tr>
              ) : null}
              {group.rows.map(manager => (
                <tr key={manager.id}>
                  <td>{manager.firstName} {manager.lastName}</td>
                  <td className="director-managers-table-col-role">{roleLabel(manager.managerRoleKey)}</td>
                  <td>{manager.roleTitle}</td>
                  <td className="director-managers-table-col-phone">
                    <div className="director-managers-phone-cell">
                      <span>{formatPhoneDisplay(manager.mobilePhone)}</span>
                      <StatusPill tone={manager.mobilePhone ? 'active' : 'inactive'}>
                        {manager.mobilePhone ? 'SMS Ready' : 'Phone Missing'}
                      </StatusPill>
                    </div>
                  </td>
                  <td className="director-managers-table-col-location">{manager.locationCode}</td>
                  <td className="director-managers-table-col-date">{manager.hireDate || 'Not set'}</td>
                  <td className="director-managers-table-col-status">
                    <StatusPill tone={manager.status}>
                      {manager.status}
                    </StatusPill>
                  </td>
                  <td className="director-managers-table-col-window">{manager.activeFrom}{manager.inactiveFrom ? ` to ${manager.inactiveFrom}` : ''}</td>
                  {showWorkforceClassification ? (
                    <td
                      className="director-managers-table-col-classification"
                      data-testid={`manager-classification-${manager.id}`}
                    >
                      {manager.linkedEmploymentClassification === 'hourly'
                        ? 'Hourly'
                        : manager.linkedEmploymentClassification === 'salary'
                          ? 'Salary'
                          : <span aria-label="No linked profile">—</span>}
                    </td>
                  ) : null}
                  <td className="director-managers-table-col-actions">
                    <div className="director-managers-action-group">
                      {canViewEmployeeProfile && Number.isFinite(Number(manager.id)) && Number(manager.id) > 0
                        ? (() => {
                            const targetPath = buildPersonWorkspacePath({ managerId: manager.id });
                            return (
                              <a
                                className="ui-button ui-button--sm ui-button--secondary"
                                href={targetPath}
                                data-testid={`manager-view-profile-${manager.id}`}
                                onClick={(event) => {
                                  // Issue-fix (re-fix): the previous patch used
                                  // react-router's navigate() which calls
                                  // history.pushState. App.js keeps its OWN
                                  // currentPath state and updates it only on
                                  // popstate or via its onNavigate callback —
                                  // pushState does NOT fire popstate, so the
                                  // URL changed but App.js's render tree stayed
                                  // on the previous path. Result: left-click did
                                  // nothing visible. Calling the parent-provided
                                  // onNavigate (DirectorDashboard passes it
                                  // through from App.js) updates currentPath
                                  // correctly so the workforce profile route
                                  // mounts. Cmd / ctrl / shift / middle click
                                  // bypass the handler and hit the native href
                                  // so "open in new tab" continues to work.
                                  if (
                                    event.metaKey
                                    || event.ctrlKey
                                    || event.shiftKey
                                    || event.altKey
                                    || event.button === 1
                                  ) {
                                    return;
                                  }
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (typeof onNavigate === 'function') {
                                    onNavigate(targetPath);
                                  } else {
                                    // Fallback: if the component is mounted
                                    // somewhere without the onNavigate plumbing,
                                    // fall back to React Router so the URL still
                                    // updates. The downstream render may stay
                                    // stale until App.js is also wired, but the
                                    // URL bar reflects the intent.
                                    navigate(targetPath);
                                  }
                                }}
                              >
                                View profile
                              </a>
                            );
                          })()
                        : null}
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleOpenEditModal(manager)}
                      >
                        Edit Leadership Role
                      </Button>
                      <Button
                        size="sm"
                        variant={manager.status === 'active' ? 'danger' : 'secondary'}
                        onClick={() => handleToggleManager(manager)}
                      >
                        {manager.status === 'active' ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </DataTable>
      </section>

      {editingManagerId ? (
        <DirectorModal
          title="Edit Leadership Role"
          badge="Roster"
          onClose={closeEditModal}
        >
          <form className="director-form-grid" onSubmit={handleSaveManagerEdit}>
            <label>
              First Name
              <input value={editForm.firstName} onChange={(event) => handleEditFormChange('firstName', event.target.value)} required />
            </label>
            <label>
              Last Name
              <input value={editForm.lastName} onChange={(event) => handleEditFormChange('lastName', event.target.value)} required />
            </label>
            <label>
              Email
              <input type="email" value={editForm.email} onChange={(event) => handleEditFormChange('email', event.target.value)} />
            </label>
            <label>
              Mobile Phone
              <input
                value={editForm.mobilePhone}
                onChange={(event) => handleEditFormChange('mobilePhone', event.target.value)}
                placeholder="+1 555 555 5555"
              />
            </label>
            <label>
              Location
              <select value={editForm.locationId} onChange={(event) => handleEditFormChange('locationId', event.target.value)} required>
                <option value="">Select location</option>
                {locations.filter(location => location.isActive || String(location.id) === editForm.locationId).map(location => (
                  <option key={location.id} value={location.id}>{location.code} - {location.name}</option>
                ))}
              </select>
            </label>
            <label>
              Leadership Role
              <select value={editForm.managerRoleKey} onChange={(event) => handleEditFormChange('managerRoleKey', event.target.value)}>
                {MANAGER_ROLE_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
              <small className="director-managers-leadership-role-helper">
                Leadership roles own location KPIs, goals, and scorecards.
                For hourly and non-leadership employees, use Workforce.
              </small>
            </label>
            <label>
              Role Title
              <input value={editForm.roleTitle} onChange={(event) => handleEditFormChange('roleTitle', event.target.value)} required />
            </label>
            <label>
              Department
              <input value={editForm.department} onChange={(event) => handleEditFormChange('department', event.target.value)} />
            </label>
            <label>
              Hire Date
              <input type="date" value={editForm.hireDate} onChange={(event) => handleEditFormChange('hireDate', event.target.value)} />
            </label>
            <div className="director-note-card director-form-span">
              <strong>SMS Intake Readiness</strong>
              <div className="director-muted">
                Managers with a saved mobile number can be matched automatically when they text the attendance number.
              </div>
            </div>
            <div className="director-note-card director-form-span">
              <strong>Manager Key</strong>
              <div className="director-muted">
                A generated roster identifier used to match CSV re-imports and link user access to this manager record.
                Regenerating replaces it immediately — anything still matching against the old key will need updating.
              </div>
              <div className="director-form-actions">
                <Button size="sm" variant="danger" onClick={handleRegenerateManagerKey}>
                  Regenerate Manager Key
                </Button>
              </div>
            </div>
            <div className="director-form-actions director-form-actions-spread director-form-span">
              <Button size="sm" variant="secondary" onClick={closeEditModal}>
                Cancel
              </Button>
              <Button type="submit" loading={isEditSubmitting} loadingLabel="Saving…">
                Save Manager
              </Button>
            </div>
          </form>
        </DirectorModal>
      ) : null}
    </div>
  );
}

export default DirectorManagersPanel;
